import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import api from '../lib/api';
import { Package, Truck, Layers, AlertTriangle, ArrowDown, Star, Clock } from 'lucide-react';

const QUALITY_COLORS = {
  Premium: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Standard: 'bg-blue-100 text-blue-700 border-blue-200',
  Economy: 'bg-amber-100 text-amber-700 border-amber-200',
};

const StockLotsDialog = ({ open, onOpenChange, articleId, articleName, binId, onConsumed }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consumeMode, setConsumeMode] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);
  const [consumeQty, setConsumeQty] = useState('');
  const [consuming, setConsuming] = useState(false);
  const [strategy, setStrategy] = useState('fifo'); // fifo or manual

  const fetchLots = useCallback(async () => {
    if (!articleId) return;
    setLoading(true);
    try {
      const params = binId ? `?bin_id=${binId}` : '';
      const res = await api.get(`/stock/lots/${articleId}${params}`);
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea loturilor');
    } finally {
      setLoading(false);
    }
  }, [articleId, binId]);

  useEffect(() => {
    if (open && articleId) fetchLots();
  }, [open, articleId, fetchLots]);

  const handleConsume = async () => {
    const qty = parseFloat(consumeQty);
    if (!qty || qty <= 0) { toast.error('Introduceti o cantitate valida'); return; }
    if (!binId && strategy === 'manual' && !selectedLot) { toast.error('Selectati un lot'); return; }

    setConsuming(true);
    try {
      const payload = {
        article_id: articleId,
        bin_id: binId || data?.lots?.[0]?.bin_id,
        quantity: qty,
        reference_type: 'manual',
        notes: strategy === 'manual' ? `Consum manual din lot ${selectedLot}` : 'Consum FIFO automat',
      };
      if (strategy === 'manual' && selectedLot) payload.lot_number = selectedLot;
      else payload.consumption_strategy = 'fifo';

      const res = await api.post('/stock/issue', payload);
      const consumed = res.data.consumed_lots;
      if (consumed?.length) {
        const details = consumed.map(c => `${c.lot_number}: ${c.quantity}`).join(', ');
        toast.success(`Consumat din: ${details}`);
      } else {
        toast.success('Consum inregistrat!');
      }
      setConsumeQty('');
      setSelectedLot(null);
      setConsumeMode(false);
      fetchLots();
      onConsumed?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la consum');
    } finally {
      setConsuming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="stock-lots-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#0052CC]" />
            Loturi stoc: {articleName || data?.article?.name}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Vizualizare stoc per furnizor si lot cu ordinea FIFO
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">Se incarca...</div>
        ) : data ? (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{data.total_stock}</p>
                <p className="text-xs text-slate-500">{data.article?.unit || 'buc'} total</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{data.by_supplier?.length || 0}</p>
                <p className="text-xs text-slate-500">Furnizori</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{data.lots?.length || 0}</p>
                <p className="text-xs text-slate-500">Loturi active</p>
              </div>
            </div>

            {/* By supplier breakdown */}
            {data.by_supplier?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Truck className="w-4 h-4" /> Stoc per furnizor
                </h3>
                <div className="space-y-2">
                  {data.by_supplier.map((s, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border bg-white" data-testid={`supplier-row-${i}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#0052CC]/10 flex items-center justify-center text-[#0052CC] font-bold text-xs">
                          {s.supplier_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{s.supplier_name}</p>
                          <Badge className={`text-[10px] ${QUALITY_COLORS[s.quality_grade] || QUALITY_COLORS.Standard}`}>
                            {s.quality_grade}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{s.total_quantity}</p>
                        <p className="text-[10px] text-slate-500">{s.lots_count} lot(uri)</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Individual lots (FIFO order) */}
            {data.lots?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Loturi (ordine FIFO)
                </h3>
                <div className="space-y-1">
                  {data.lots.map((lot, i) => (
                    <div 
                      key={i} 
                      className={`flex items-center justify-between p-2.5 rounded-lg border text-sm cursor-pointer transition-colors ${
                        selectedLot === lot.lot_number ? 'border-[#0052CC] bg-blue-50' : 'bg-white hover:bg-slate-50'
                      } ${i === 0 ? 'border-l-4 border-l-emerald-500' : ''}`}
                      onClick={() => { setSelectedLot(lot.lot_number); setStrategy('manual'); }}
                      data-testid={`lot-row-${i}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-xs font-mono text-slate-500">#{lot.fifo_order}</span>
                          {i === 0 && <Clock className="w-3 h-3 text-emerald-600" title="Cel mai vechi (FIFO)" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs truncate">{lot.lot_number}</p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-500">
                            <span>{lot.supplier_name}</span>
                            <Badge className={`text-[8px] py-0 ${QUALITY_COLORS[lot.quality_grade] || QUALITY_COLORS.Standard}`}>
                              {lot.quality_grade}
                            </Badge>
                            {lot.bin_code && <span className="text-slate-400">{lot.bin_code}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-2">
                        <p className="font-bold">{lot.quantity}</p>
                        {lot.entry_date && <p className="text-[10px] text-slate-400">{new Date(lot.entry_date).toLocaleDateString('ro-RO')}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.lots?.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p>Nu exista loturi active pentru acest articol</p>
              </div>
            )}

            <Separator />

            {/* Consume section */}
            {!consumeMode ? (
              <Button onClick={() => setConsumeMode(true)} className="w-full bg-[#0052CC]" data-testid="lots-consume-btn">
                <ArrowDown className="w-4 h-4 mr-2" /> Consum din stoc
              </Button>
            ) : (
              <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-testid="lots-consume-form">
                <h4 className="text-sm font-semibold">Consum stoc</h4>
                <div className="flex gap-3 items-center">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" checked={strategy === 'fifo'} onChange={() => { setStrategy('fifo'); setSelectedLot(null); }} className="w-4 h-4" />
                    FIFO (automat)
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" checked={strategy === 'manual'} onChange={() => setStrategy('manual')} className="w-4 h-4" />
                    Manual (selecteaza lot)
                  </label>
                </div>
                {strategy === 'manual' && selectedLot && (
                  <p className="text-xs text-[#0052CC]">Lot selectat: <strong>{selectedLot}</strong></p>
                )}
                {strategy === 'manual' && !selectedLot && (
                  <p className="text-xs text-amber-600">Selectati un lot din lista de mai sus</p>
                )}
                <div className="flex gap-2">
                  <Input
                    type="number" min="1" placeholder="Cantitate"
                    value={consumeQty} onChange={e => setConsumeQty(e.target.value)}
                    className="flex-1 h-9" data-testid="lots-consume-qty"
                  />
                  <Button onClick={handleConsume} disabled={consuming} className="bg-red-600 hover:bg-red-700" data-testid="lots-consume-confirm">
                    {consuming ? 'Se proceseaza...' : 'Consuma'}
                  </Button>
                  <Button variant="outline" onClick={() => { setConsumeMode(false); setSelectedLot(null); }}>Anuleaza</Button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export { StockLotsDialog };
